import { useState } from "react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { Column as ColumnType } from "@/types/board";
import Card from "./Card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  MoreHorizontal,
  Plus,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Loader2,
  CheckCircle2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { auth } from "@/lib/firebase";
import { api } from "@/lib/api";
import { boardEvents, BOARD_UPDATED } from "@/lib/events";
import { EnhancedNoteEditor } from "@/components/EnhancedNoteEditor";
import type { NoteType } from "@/types/board";
import { format } from "date-fns";
import { Calendar } from "lucide-react";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface ColumnProps {
  column: ColumnType;
  index: number;
  onEditCard: (columnId: string, cardId: string, title: string, description: string) => void;
  onDeleteCard: (columnId: string, cardId: string) => void;
  onEditColumn: (columnId: string, title: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onPriorityChange: (cardId: string, priority: 'critical' | 'important' | 'normal') => void;
  onStatusChange: (cardId: string, newStatus: string) => void;
}

export default function Column({
  column,
  index,
  onEditCard,
  onDeleteCard,
  onEditColumn,
  onDeleteColumn,
  onPriorityChange,
  onStatusChange,
}: ColumnProps) {
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState("");
  const [newCardDescription, setNewCardDescription] = useState("");
  const [noteType, setNoteType] = useState<NoteType>("general");
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isEditingColumn, setIsEditingColumn] = useState(false);
  const [editColumnTitle, setEditColumnTitle] = useState(column.title);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { toast: _toast } = useToast();

  const handleAddCard = async () => {
    if (!newCardTitle.trim()) return;

    const user = auth.currentUser;
    if (!user) return;

    try {
      setIsLoading(true);
      await api.post('/cards', {
        column_id:   column.id,
        title:       newCardTitle,
        description: newCardDescription || null,
        due_date:    dueDate ? dueDate.toISOString().split('T')[0] : null,
        status:      'todo',
      });

      boardEvents.emit(BOARD_UPDATED);
      setNewCardTitle("");
      setNewCardDescription("");
      setNoteType("general");
      setDueDate(undefined);
      setIsAddingCard(false);
    } catch (error) {
      console.error('Error creating card:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditColumn = () => {
    if (!editColumnTitle.trim()) return;
    onEditColumn(column.id, editColumnTitle.trim());
    setIsEditingColumn(false);
  };

  const getColumnColor = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('done') || lowerTitle.includes('completed')) {
      return 'bg-green-400/30 hover:bg-green-400/40';
    }
    if (lowerTitle.includes('progress') || lowerTitle.includes('doing')) {
      return 'bg-yellow-400/30 hover:bg-yellow-400/40';
    }
    if (lowerTitle.includes('to do') || lowerTitle.includes('todo')) {
      return 'bg-red-400/30 hover:bg-red-400/40';
    }
    return 'bg-indigo-400/30 hover:bg-indigo-400/40';
  };

  const getColumnIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('done') || lowerTitle.includes('completed')) {
      return <CheckCircle2 className="h-5 w-5 text-green-400" />;
    }
    if (lowerTitle.includes('progress') || lowerTitle.includes('doing')) {
      return <Loader2 className="h-5 w-5 text-yellow-400 animate-spin" />;
    }
    return <CheckSquare className="h-5 w-5 text-red-400" />;
  };

  const getHeaderGradient = (title: string): string => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('done') || lowerTitle.includes('completed')) {
      return 'bg-gradient-to-r from-green-500/20 to-green-400/10';
    }
    if (lowerTitle.includes('progress') || lowerTitle.includes('doing')) {
      return 'bg-gradient-to-r from-yellow-500/20 to-yellow-400/10';
    }
    if (lowerTitle.includes('to do') || lowerTitle.includes('todo')) {
      return 'bg-gradient-to-r from-red-500/20 to-red-400/10';
    }
    return 'bg-gradient-to-r from-indigo-500/20 to-indigo-400/10';
  };

  return (
    <Draggable draggableId={column.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`
            w-full
            flex-shrink-0 rounded-lg 
            ${getColumnColor(column.title)}
            ${snapshot.isDragging ? 'shadow-lg ring-2 ring-indigo-500/50' : ''} 
            border-2 border-white/20 
            hover:border-white/30 
            shadow-lg shadow-black/20
            transform-gpu
            hover:translate-y-[-2px]
            hover:shadow-xl
            group
            md:min-w-[300px] md:w-[300px]
          `}
        >
          <div className={`
            flex items-center justify-between 
            px-3 py-2 rounded-t-lg 
            ${getHeaderGradient(column.title)}
            transition-colors duration-200
            group-hover:bg-opacity-90
          `}>
            <div className="flex items-center space-x-2">
              <div className="transform transition-transform duration-200 group-hover:scale-110">
                {getColumnIcon(column.title)}
              </div>
              <h2 className="text-lg font-semibold text-white transition-opacity duration-200 group-hover:opacity-90">
                {column.title}
              </h2>
              <span className="
                px-2 py-0.5 
                rounded-full 
                bg-white/10 
                text-sm text-white/80
                transition-all duration-200
                group-hover:bg-white/15
                group-hover:translate-x-1
              ">
                {column.cards.length}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                className={`
                  h-8 w-8 p-0 
                  text-white hover:text-white hover:bg-white/10 
                  transition-all duration-200
                  transform-gpu
                  hover:scale-110
                  ${isExpanded ? 'bg-white/10' : ''}
                `}
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="
                      h-8 w-8 p-0 
                      text-white hover:text-white hover:bg-white/10
                      transition-transform duration-200
                      hover:scale-110
                    "
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setIsEditingColumn(true)}>
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-red-600"
                    onClick={() => onDeleteColumn(column.id)}
                  >
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <div className="p-2">
            <Droppable droppableId={column.id} type="CARD">
              {(droppableProvided) => (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className={`
                    space-y-2 
                    ${isExpanded ? 'max-h-[calc(8*5.5rem)]' : 'max-h-[calc(5*5.5rem)]'} 
                    overflow-y-auto 
                    scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent
                    hover:scrollbar-thumb-white/20
                  `}
                >
                  {column.cards.map((card, cardIndex) => (
                    <Card
                      key={card.id}
                      card={card}
                      index={cardIndex}
                      columnId={column.id}
                      onEdit={onEditCard}
                      onDelete={onDeleteCard}
                      onPriorityChange={onPriorityChange}
                      onStatusChange={onStatusChange}
                    />
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>

            <Button
              onClick={() => setIsAddingCard(true)}
              className="w-full mt-2 bg-gray-900/60 hover:bg-gray-800/70 text-gray-200 font-medium border-2 border-white/20 hover:border-white/30"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Card
            </Button>
          </div>

          <Dialog open={isAddingCard} onOpenChange={setIsAddingCard}>
            <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] flex flex-col overflow-hidden rounded-xl">
              <DialogHeader>
                <DialogTitle>Add New Card</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4 flex-1 overflow-hidden">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Enter card title"
                    value={newCardTitle}
                    onChange={(e) => setNewCardTitle(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal ${!dueDate && "text-muted-foreground"}`}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {dueDate ? format(dueDate, "PPP") : "Set due date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPicker
                        mode="single"
                        selected={dueDate}
                        onSelect={(date: Date | undefined) => {
                          setDueDate(date);
                          setIsDatePickerOpen(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="overflow-y-auto pr-2">
                  <EnhancedNoteEditor
                    value={newCardDescription}
                    onChange={setNewCardDescription}
                    noteType={noteType}
                    onNoteTypeChange={setNoteType}
                  />
                </div>
              </div>
              <DialogFooter className="border-t bg-background py-2">
                <Button variant="outline" onClick={() => setIsAddingCard(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddCard} disabled={isLoading}>
                  {isLoading ? "Adding..." : "Add Card"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={isEditingColumn} onOpenChange={setIsEditingColumn}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Column</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Enter column title"
                    value={editColumnTitle}
                    onChange={(e) => setEditColumnTitle(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditingColumn(false)}>
                  Cancel
                </Button>
                <Button onClick={handleEditColumn}>Save Changes</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
}